import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DataTable,
  FilterSelect,
  FormControlLabel,
  HairlineCard,
  Icons,
  iconSize,
  palette,
  RowAction,
  RowActions,
  Stack,
  StatusChip,
  statusTone,
  Tab,
  Tabs,
  TextField,
  Typography,
  fontStacks,
  type LucideIcon,
  useRowActionsColumn,
} from '@munifocus/ui';
import {
  TICKET_STATUSES,
  ticketBalanceCents,
  type FineScheduleEntry,
  type TicketLgSummary,
  type TicketListItem,
  type TicketStatus,
} from '@munifocus/contracts-gov';
import { parseMoneyToCents } from '@munifocus/billing';
import { AppShell } from '../components/AppShell';
import { ModuleOffNotice } from '../components/ModuleOffNotice';
import { PageIntro } from '../components/PageIntro';
import { TicketActionDialog } from '../components/TicketsPanel';
import { useScope } from '../providers/ScopeProvider';
import { formatDate } from '../lib/relative-time';
import {
  formatMoney,
  offenceLabel,
  ticketActions,
  useFineSchedule,
  useSaveFineScheduleEntry,
  useTicketSummary,
  useTickets,
  type TicketAction,
} from '../lib/tickets-api';
import { frenchFirstOfMonth } from '@munifocus/utils';

/**
 * The municipality's fines: what is owed, what is overdue, what has gone to
 * collections, what has been collected — and every ticket, filterable by
 * status, with the moves the office makes on them. A second tab keeps the fine
 * schedule, for those who hold `fine_schedule:manage`.
 *
 * Scoped to the active municipality. Offered in the rail only where tickets
 * are turned on (see `navigation.ts`).
 */
export function Tickets() {
  const { t } = useTranslation();
  const scope = useScope();
  const lgId = scope.active?.kind === 'local_government' ? scope.active.id : undefined;
  const [tab, setTab] = useState<'tickets' | 'schedule'>('tickets');
  const canEditSchedule = scope.can('fine_schedule:manage');
  const summary = useTicketSummary();
  const mine = summary.data?.find((s) => s.localGovernmentId === lgId);

  return (
    <AppShell title={t('gov.tickets.title')}>
      <PageIntro>{t('gov.tickets.intro')}</PageIntro>
      {!lgId ? (
        <Alert severity="info">{t('gov.tickets.pickMunicipality')}</Alert>
      ) : mine && isOffAndEmpty(mine) ? (
        <ModuleOffNotice
          icon={Icons.Receipt}
          title={t('gov.tickets.moduleOff.title')}
          what={t('gov.tickets.moduleOff.what')}
          who={t('gov.tickets.moduleOff.who')}
        />
      ) : (
        <Stack spacing={2}>
          <Tabs value={tab} onChange={(_e, v) => setTab(v)} aria-label={t('gov.tickets.title')}>
            <Tab value="tickets" label={t('gov.tickets.tabTickets')} />
            <Tab value="schedule" label={t('gov.tickets.tabSchedule')} />
          </Tabs>
          {tab === 'tickets' ? (
            <TicketsTab lgId={lgId} />
          ) : (
            <ScheduleTab lgId={lgId} canEdit={canEditSchedule} />
          )}
        </Stack>
      )}
    </AppShell>
  );
}

/**
 * Switched off, with nothing ever issued: no tiles, no chart, no table — a
 * screen of $0.00 says "we collected nothing", not "this is not on". Tickets
 * issued before the plan changed are still owed and payable, so any money or
 * any ticket on the books keeps the full screen under the plain notice.
 */
function isOffAndEmpty(summary: TicketLgSummary): boolean {
  return (
    !summary.enabled &&
    summary.outstandingCount === 0 &&
    summary.overdueCount === 0 &&
    summary.disputedCount === 0 &&
    summary.collectionsCount === 0 &&
    summary.collectedCents === 0
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <HairlineCard sx={{ p: 2, flex: '1 1 160px', minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        sx={{ fontFamily: fontStacks.display, fontWeight: 700, fontSize: 22, lineHeight: 1.3 }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </HairlineCard>
  );
}

/** Collected per month, as bars — with every value printed beside its bar. */
function Revenue({ summary }: { summary: TicketLgSummary }) {
  const { t, i18n } = useTranslation();
  const max = Math.max(1, ...summary.byMonth.map((m) => m.collectedCents));
  const monthLabel = (m: string) =>
    frenchFirstOfMonth(
      new Intl.DateTimeFormat(i18n.language.startsWith('fr') ? 'fr-CA' : 'en-CA', {
        month: 'short',
        year: '2-digit',
        timeZone: 'UTC',
      }).format(new Date(`${m}-15T12:00:00Z`)),
    );
  return (
    <HairlineCard sx={{ p: 2 }}>
      <Typography component="h2" sx={{ fontWeight: 700, mb: 1.5 }}>
        {t('gov.tickets.revenueTitle')}
      </Typography>
      <Stack spacing={0.75} role="list">
        {summary.byMonth.map((m) => (
          <Stack
            key={m.month}
            direction="row"
            spacing={1.5}
            role="listitem"
            sx={{ alignItems: 'center' }}
          >
            <Typography variant="caption" sx={{ width: 64, flexShrink: 0 }}>
              {monthLabel(m.month)}
            </Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  height: 10,
                  borderRadius: 0.5,
                  width: `${(m.collectedCents / max) * 100}%`,
                  minWidth: m.collectedCents > 0 ? 2 : 0,
                  bgcolor: palette.brand.accent,
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ width: 96, textAlign: 'right', flexShrink: 0 }}>
              {formatMoney(m.collectedCents, 'CAD', i18n.language)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </HairlineCard>
  );
}

const TICKET_ACTION_ICON: Record<TicketAction['kind'], LucideIcon> = {
  serve: Icons.Send,
  payment: Icons.Banknote,
  dispute: Icons.AlertTriangle,
  decide: Icons.Gavel,
  collections: Icons.Archive,
  void: Icons.Ban,
};

function TicketsTab({ lgId }: { lgId: string }) {
  const { t, i18n } = useTranslation();
  const summary = useTicketSummary();
  const [status, setStatus] = useState<TicketStatus | 'all'>('all');
  const tickets = useTickets(lgId, status === 'all' ? [] : [status]);
  const [acting, setActing] = useState<{ ticket: TicketListItem; kind: TicketAction['kind'] }>();
  const actionsColumn = useRowActionsColumn();
  const mine = summary.data?.find((s) => s.localGovernmentId === lgId);
  const money = (c: number) => formatMoney(c, 'CAD', i18n.language);

  return (
    <Stack spacing={2}>
      {mine && !mine.enabled && <Alert severity="info">{t('gov.tickets.disabledHere')}</Alert>}
      {mine && (
        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
          <Stat
            label={t('gov.tickets.stat.outstanding')}
            value={money(mine.outstandingCents)}
            sub={t('gov.tickets.stat.count', { count: mine.outstandingCount })}
          />
          <Stat
            label={t('gov.tickets.stat.overdue')}
            value={money(mine.overdueCents)}
            sub={t('gov.tickets.stat.count', { count: mine.overdueCount })}
          />
          <Stat
            label={t('gov.tickets.stat.collections')}
            value={money(mine.collectionsCents)}
            sub={t('gov.tickets.stat.count', { count: mine.collectionsCount })}
          />
          <Stat
            label={t('gov.tickets.stat.thisMonth')}
            value={money(mine.collectedThisMonthCents)}
            sub={t('gov.tickets.stat.total', { amount: money(mine.collectedCents) })}
          />
        </Stack>
      )}
      {mine && <Revenue summary={mine} />}

      <Stack direction="row" spacing={1}>
        <FilterSelect
          label={t('gov.tickets.filterStatus')}
          value={status}
          onChange={(v) => setStatus(v as TicketStatus | 'all')}
          options={TICKET_STATUSES.map((s) => ({ value: s, label: t(`gov.tickets.status.${s}`) }))}
        />
      </Stack>
      <DataTable
        rows={tickets.data ?? []}
        getRowKey={(row) => row.id}
        isLoading={tickets.isLoading}
        emptyLabel={t('gov.tickets.listEmpty')}
        pageSize={25}
        initialSortKey="issued"
        initialSortDir="desc"
        rowHref={(row) => `/files/${row.fileId}`}
        linkComponent={RouterLink}
        search={{
          placeholder: t('gov.tickets.search'),
          of: (row) =>
            `${row.reference} ${row.fileReference} ${row.address} ${row.recipient.name ?? ''}`,
        }}
        columns={[
          {
            key: 'reference',
            label: t('gov.tickets.col.reference'),
            primary: true,
            sortValue: (row) => row.reference,
            cell: (row) => row.reference,
          },
          {
            key: 'address',
            label: t('gov.tickets.col.property'),
            sortValue: (row) => row.address,
            cell: (row) => row.address,
          },
          {
            key: 'offence',
            label: t('gov.tickets.col.offence'),
            sortValue: (row) => row.offenceCode,
            cell: (row) => `${row.offenceCode} — ${offenceLabel(row, i18n.language)}`,
          },
          {
            key: 'balance',
            label: t('gov.tickets.col.balance'),
            nowrap: true,
            sortValue: (row) => ticketBalanceCents(row),
            cell: (row) => money(ticketBalanceCents(row)),
          },
          {
            key: 'status',
            label: t('gov.tickets.col.status'),
            sortValue: (row) => row.status,
            cell: (row) => (
              <StatusChip
                status={statusTone.ticket[row.status]}
                label={t(`gov.tickets.status.${row.status}`)}
              />
            ),
          },
          {
            key: 'due',
            label: t('gov.tickets.col.due'),
            nowrap: true,
            sortValue: (row) => row.dueAt ?? '',
            cell: (row) => (row.dueAt ? formatDate(row.dueAt, i18n.language) : '—'),
          },
          {
            key: 'issued',
            label: t('gov.tickets.col.issued'),
            nowrap: true,
            sortValue: (row) => row.issuedAt,
            cell: (row) => formatDate(row.issuedAt, i18n.language),
          },
          {
            // The shared actions column: pinned right, and icon-only when the
            // table is wider than its card, so an action is never clipped.
            ...actionsColumn,
            cell: (row) => (
              <RowActions>
                {ticketActions(row)
                  .filter((k) => k !== 'serve')
                  .map((kind) => (
                    <RowAction
                      key={kind}
                      label={t(`gov.tickets.action.${kind}`)}
                      icon={TICKET_ACTION_ICON[kind]}
                      tone={kind === 'void' ? 'danger' : 'default'}
                      menu={kind === 'void'}
                      onClick={() => setActing({ ticket: row, kind })}
                    />
                  ))}
              </RowActions>
            ),
          },
        ]}
      />
      {acting && (
        <TicketActionDialog
          ticket={acting.ticket}
          kind={acting.kind}
          onClose={() => setActing(undefined)}
        />
      )}
    </Stack>
  );
}

function ScheduleTab({ lgId, canEdit }: { lgId: string; canEdit: boolean }) {
  const { t, i18n } = useTranslation();
  const schedule = useFineSchedule(lgId);
  const [editing, setEditing] = useState<FineScheduleEntry | 'new' | null>(null);
  const actionsColumn = useRowActionsColumn();

  return (
    <Stack spacing={2}>
      {canEdit && (
        <Box>
          <Button
            variant="contained"
            startIcon={<Icons.Plus size={iconSize.sm} />}
            onClick={() => setEditing('new')}
          >
            {t('gov.tickets.schedule.add')}
          </Button>
        </Box>
      )}
      <DataTable
        rows={schedule.data ?? []}
        getRowKey={(row) => row.id}
        isLoading={schedule.isLoading}
        emptyLabel={t('gov.tickets.schedule.empty')}
        pageSize={50}
        initialSortKey="code"
        columns={[
          {
            key: 'code',
            label: t('gov.tickets.schedule.code'),
            primary: true,
            sortValue: (row) => row.code,
            cell: (row) => row.code,
          },
          {
            key: 'offence',
            label: t('gov.tickets.schedule.offence'),
            sortValue: (row) => offenceLabel(row, i18n.language),
            cell: (row) => offenceLabel(row, i18n.language),
          },
          {
            key: 'section',
            label: t('gov.tickets.schedule.section'),
            sortValue: (row) => row.bylawSection,
            cell: (row) => row.bylawSection || '—',
          },
          {
            key: 'amount',
            label: t('gov.tickets.schedule.amount'),
            nowrap: true,
            sortValue: (row) => row.amountCents,
            cell: (row) => formatMoney(row.amountCents, 'CAD', i18n.language),
          },
          {
            key: 'days',
            label: t('gov.tickets.schedule.days'),
            sortValue: (row) => row.paymentDays,
            cell: (row) => String(row.paymentDays),
          },
          {
            key: 'active',
            label: t('gov.tickets.schedule.state'),
            sortValue: (row) => (row.isActive ? 1 : 0),
            cell: (row) => (
              <StatusChip
                status={row.isActive ? 'success' : 'neutral'}
                label={t(
                  row.isActive ? 'gov.tickets.schedule.active' : 'gov.tickets.schedule.retired',
                )}
              />
            ),
          },
          ...(canEdit
            ? [
                {
                  ...actionsColumn,
                  cell: (row: FineScheduleEntry) => (
                    <RowActions>
                      <RowAction
                        label={t('gov.tickets.schedule.edit')}
                        icon={Icons.Pencil}
                        onClick={() => setEditing(row)}
                      />
                    </RowActions>
                  ),
                },
              ]
            : []),
        ]}
      />
      {editing && (
        <ScheduleEntryDialog
          lgId={lgId}
          entry={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </Stack>
  );
}

function ScheduleEntryDialog({
  lgId,
  entry,
  onClose,
}: {
  lgId: string;
  entry: FineScheduleEntry | null;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const save = useSaveFineScheduleEntry(lgId);
  const [code, setCode] = useState(entry?.code ?? '');
  const [offenceEn, setOffenceEn] = useState(entry?.offenceEn ?? '');
  const [offenceFr, setOffenceFr] = useState(entry?.offenceFr ?? '');
  const [section, setSection] = useState(entry?.bylawSection ?? '');
  const [amount, setAmount] = useState(entry ? (entry.amountCents / 100).toFixed(2) : '');
  const [days, setDays] = useState(String(entry?.paymentDays ?? 30));
  const [active, setActive] = useState(entry?.isActive ?? true);
  const cents = parseMoneyToCents(amount, i18n.language.startsWith('fr') ? 'fr-CA' : 'en-CA');
  const dayCount = Number(days);
  const valid =
    (entry || /^[A-Za-z0-9][A-Za-z0-9._-]{0,31}$/.test(code.trim())) &&
    offenceEn.trim().length >= 3 &&
    offenceFr.trim().length >= 3 &&
    cents !== null &&
    cents > 0 &&
    Number.isInteger(dayCount) &&
    dayCount >= 1 &&
    dayCount <= 365;

  function submit() {
    if (!valid || cents === null) return;
    const common = {
      offenceEn: offenceEn.trim(),
      offenceFr: offenceFr.trim(),
      bylawSection: section.trim(),
      amountCents: cents,
      paymentDays: dayCount,
      isActive: active,
    };
    save.mutate(entry ? { id: entry.id, dto: common } : { dto: { ...common, code: code.trim() } }, {
      onSuccess: onClose,
    });
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {entry ? t('gov.tickets.schedule.editTitle') : t('gov.tickets.schedule.addTitle')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label={t('gov.tickets.schedule.code')}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={!!entry}
            helperText={t('gov.tickets.schedule.codeHint')}
          />
          <TextField
            label={t('gov.tickets.schedule.offenceEn')}
            value={offenceEn}
            onChange={(e) => setOffenceEn(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('gov.tickets.schedule.offenceFr')}
            value={offenceFr}
            onChange={(e) => setOffenceFr(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('gov.tickets.schedule.section')}
            value={section}
            onChange={(e) => setSection(e.target.value)}
          />
          <TextField
            label={t('gov.tickets.schedule.amount')}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            error={amount !== '' && (cents === null || cents <= 0)}
          />
          <TextField
            label={t('gov.tickets.schedule.days')}
            value={days}
            onChange={(e) => setDays(e.target.value)}
            type="number"
          />
          <FormControlLabel
            control={<Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} />}
            label={t('gov.tickets.schedule.activeLabel')}
          />
          {entry && (
            <Typography variant="caption" color="text.secondary">
              {t('gov.tickets.schedule.editHint')}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('gov.tickets.cancel')}</Button>
        <Button variant="contained" disabled={!valid || save.isPending} onClick={submit}>
          {t('gov.tickets.schedule.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
